import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LatestTransactionRow } from '@kempoweroyj/ngx-evse-api-client';
import { Transaction } from '@kempoweroyj/ngx-transactions-api-client';
import { Vehicle } from '@kempoweroyj/ngx-vehicles-api-client';
import { AuthService } from 'providers/auth.service';
import { BehaviorSubject, Observable, of, combineLatest } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { VehiclesService } from 'shared/stations-service/vehicles.service';
import { CustomerPrivacyService } from 'layout/customer-privacy/customer-privacy.service';
import { TimeDisplayService } from 'shared/time-display-service/time-display.service';

@Component({
    selector: 'ce-transaction-badge',
    templateUrl: './transaction-badge.component.html',
    styleUrls: ['./transaction-badge.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class TransactionBadgeComponent implements OnInit {
    @Input() headerStyle = false;
    @Input() tx: Transaction | LatestTransactionRow;
    @Input() showEvName = true;
    @Input() compact = false;
    @Input() endedAsFaded = false;
    @Input() showUtcLabel = false;

    isCharging = false;
    credible = true;

    hideEvInfo$ = new BehaviorSubject<boolean>(true);
    tx$: BehaviorSubject<Transaction | LatestTransactionRow> = new BehaviorSubject<Transaction | LatestTransactionRow>(
        null
    );
    ev$: Observable<Vehicle>;

    possibleEvName: string;

    customerPrivacyEnabled: boolean;

    currentTime$ = this.timeDisplayService.timeObservable$;

    constructor(
        private cdRef: ChangeDetectorRef,
        private vehicles: VehiclesService,
        private authService: AuthService,
        private customerPrivacy: CustomerPrivacyService,
        private timeDisplayService: TimeDisplayService
    ) {}

    ngOnInit(): void {
        this.authService
            .hasAccessTo$('ev.access')
            .pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe((allow) => {
                this.hideEvInfo$.next(!allow); // notify the ev$ observable that permissions changed

                this.cdRef.markForCheck();
            });

        this.customerPrivacy.enabled$.pipe(untilDestroyed(this)).subscribe((enabled) => {
            this.customerPrivacyEnabled = enabled;
            this.cdRef.markForCheck();
        });

        this.ev$ = combineLatest([this.hideEvInfo$, this.tx$]).pipe(
            switchMap(([hide, tx]) => {
                let returnedEv$: Observable<Vehicle>;

                if (hide) {
                    returnedEv$ = of(null);
                } else {
                    this.possibleEvName = tx?.evIdentify?.displayName;
                    this.cdRef.markForCheck();

                    returnedEv$ = this.vehicles.vehicleByMAC$(tx?.evMacAddress);
                }

                return returnedEv$;
            })
        );
    }

    ngOnChanges() {
        const CREDIBLE_THRESHOLD_MS = 2 * 60 * 1000;
        if (!this.tx?.updateTime) {
            this.credible = true;
        } else if (timeDiff(this.tx?.updateTime) < CREDIBLE_THRESHOLD_MS) {
            this.credible = true;
        } else {
            this.credible = false;
        }
        this.isCharging = this.tx?.status === 'Charging' && this.credible;

        this.tx$.next(this.tx); // notify the ev$ observable that the tx has changed

        this.cdRef.markForCheck();
    }
}

function timeDiff(ts: string, referenceTime?: string) {
    const reference = referenceTime ? Date.parse(referenceTime) : Date.now();
    return Math.abs(reference - Date.parse(ts));
}
